/**
 * Nickname Service
 * Manages nickname uniqueness and validation
 */

import { collection, query, where, getDocs, doc, setDoc, updateDoc, getDoc } from 'firebase/firestore'
import { db } from '../lib/firebase'
import type { PublicProfile, AchievementLevel } from '../types'

/**
 * Check if a nickname is already taken by another user
 */
export async function isNicknameAvailable(nickname: string, currentUserId?: string): Promise<boolean> {
  if (!nickname || nickname.trim().length === 0) {
    return true // Empty nickname is always available
  }

  const normalizedNickname = nickname.trim().toLowerCase()

  try {
    const profilesQuery = query(
      collection(db, 'publicProfiles'),
      where('nickname', '==', normalizedNickname)
    )

    const querySnapshot = await getDocs(profilesQuery)

    // If no documents found, nickname is available
    if (querySnapshot.empty) {
      return true
    }

    // If found, check if it belongs to the current user
    if (currentUserId) {
      const existingProfile = querySnapshot.docs[0]?.data() as PublicProfile
      return existingProfile.userId === currentUserId
    }

    // Nickname is taken by someone else
    return false
  } catch (error) {
    console.error('Error checking nickname availability:', error)
    throw new Error('No se pudo verificar la disponibilidad del nickname')
  }
}

/**
 * Create or update a user's public profile
 */
export async function updatePublicProfile(
  userId: string,
  data: {
    firstName?: string
    lastName?: string
    nickname?: string | null
    photoURL?: string | null
    showLoyaltyFrame?: boolean
  }
): Promise<void> {
  try {
    const displayName = data.nickname ||
      `${data.firstName || ''} ${data.lastName || ''}`.trim() ||
      'Usuario'

    // Helper function to remove undefined values
    const removeUndefined = (obj: any) => {
      return Object.keys(obj).reduce((acc, key) => {
        if (obj[key] !== undefined) {
          acc[key] = obj[key]
        }
        return acc
      }, {} as any)
    }

    const publicProfile = removeUndefined({
      userId,
      displayName,
      nickname: data.nickname || null,
      firstName: data.firstName,
      lastName: data.lastName,
      photoURL: data.photoURL || null,
      createdAt: new Date(),
      showLoyaltyFrame: data.showLoyaltyFrame !== undefined ? data.showLoyaltyFrame : true
    })

    // Use userId as document ID for easy lookups
    const profileDoc = doc(db, 'publicProfiles', userId)
    await setDoc(profileDoc, publicProfile, { merge: true })
  } catch (error) {
    console.error('Error updating public profile:', error)
    throw new Error('No se pudo actualizar el perfil público')
  }
}

/**
 * Validate nickname format
 */
export function validateNicknameFormat(nickname: string): string | null {
  if (!nickname || nickname.trim().length === 0) {
    return null // Empty is valid
  }

  const trimmed = nickname.trim()

  if (trimmed.length < 3) {
    return 'El nickname debe tener al menos 3 caracteres'
  }

  if (trimmed.length > 20) {
    return 'El nickname no puede tener más de 20 caracteres'
  }

  if (!/^[a-zA-Z0-9_-]+$/.test(trimmed)) {
    return 'Solo se permiten letras, números, guiones y guiones bajos'
  }

  return null // Valid
}

/**
 * Update loyalty level in user's public profile
 */
export async function updatePublicProfileLoyalty(
  userId: string,
  loyaltyLevel: AchievementLevel
): Promise<void> {
  try {
    const profileDoc = doc(db, 'publicProfiles', userId)
    await updateDoc(profileDoc, {
      loyaltyLevel
    })
  } catch (error) {
    console.error('Error updating public profile loyalty level:', error)
    throw new Error('No se pudo actualizar el nivel de lealtad del perfil público')
  }
}

/**
 * Update loyalty frame visibility preference in user's public profile
 */
export async function updatePublicProfileLoyaltyFrame(
  userId: string,
  showLoyaltyFrame: boolean
): Promise<void> {
  try {
    const profileDoc = doc(db, 'publicProfiles', userId)
    await updateDoc(profileDoc, {
      showLoyaltyFrame
    })
  } catch (error) {
    console.error('Error updating public profile loyalty frame preference:', error)
    throw new Error('No se pudo actualizar la preferencia de marco de lealtad del perfil público')
  }
}

/**
 * Get user's loyalty frame preference from public profile
 */
export async function getUserLoyaltyFramePreference(userId: string): Promise<boolean> {
  try {
    const profileDoc = doc(db, 'publicProfiles', userId)
    const docSnap = await getDoc(profileDoc)

    if (docSnap.exists()) {
      const data = docSnap.data() as PublicProfile
      return data.showLoyaltyFrame !== false // Default to true if not specified
    }

    return true // Default to true if profile doesn't exist
  } catch (error) {
    console.error('Error getting loyalty frame preference:', error)
    return true // Default to true on error
  }
}